import React from 'react'
import type { GestureResponderEvent, StyleProp, TextStyle, ViewStyle } from 'react-native'
import { Pressable, Text, View } from 'react-native'
import { twMerge } from 'tailwind-merge'

import Spinner from '@ui-kit/components/Spinner'
import { buttonLabel } from '@ui-kit/core/typography'
import { bgColor, borderColor } from '@ui-kit/styles/colors'
import type { Color } from '@ui-kit/types'

import * as styles from './styles'
import type { LabelProps, NativeProps } from './types'

const ButtonContent = (
  props: LabelProps & {
    labelColor: Color
    iconContainerClass: string
    style?: StyleProp<TextStyle> & StyleProp<ViewStyle>
  }
) => (
  <>
    {props.iconLeft ? (
      <View style={props.style} className={props.iconContainerClass}>
        {props.iconLeft}
      </View>
    ) : null}
    <Text style={props.style} className={buttonLabel({ color: props.labelColor })}>
      {props.label}
    </Text>
    {props.iconRight ? (
      <View style={props.style} className={props.iconContainerClass}>
        {props.iconRight}
      </View>
    ) : null}
  </>
)

const Button = (props: NativeProps): JSX.Element => {
  const labelColor = styles.getLabelColor(props.type, props.variantColor)
  const variantColor = props.variantColor ?? 'brandDark'
  const type = props.type ?? 'default'

  const onButtonPress = (e: GestureResponderEvent) => {
    if (props.isLoading || props.disabled) {
      return
    }

    props.onPress?.(e)
  }

  const { container, labelContainer, iconContainer } = styles.button({
    disabled: props.disabled,
    size: props.size,
    isLoading: props.isLoading,
    type,
  })

  return (
    <Pressable
      key={props.id}
      testID={props.id}
      className={container({
        className: twMerge(
          type === 'default' && bgColor({ color: variantColor }),
          type === 'outlined' && ['border border-solid', borderColor({ color: variantColor })]
        ),
      })}
      onPress={onButtonPress}
      disabled={props.disabled}
      style={props.style}
    >
      {props.isLoading && <Spinner fullScreen color={labelColor} size="small" />}
      <View className={labelContainer()}>
        {'icon' in props ? (
          <View className={iconContainer()}>{props.icon}</View>
        ) : (
          <ButtonContent
            label={props.label ?? ''}
            labelColor={labelColor}
            iconRight={props.iconRight}
            iconLeft={props.iconLeft}
            iconContainerClass={iconContainer()}
          />
        )}
      </View>
    </Pressable>
  )
}

export default Button
